import React, { useState, useEffect } from 'react';
import { updateQuote, archiveQuote } from '../api';
import './quoteorder.css';
import { useNavigate } from 'react-router-dom';
import NewWorkOrderModal from '../newworkordermodal';
import PrintQuote from '../PrintQuote';
import { formatDateTimeDisplay } from '../../utils/timezone';

const QuoteOrder = ({ quote, onClose }) => {
  const [editedQuote, setEditedQuote] = useState(quote ? { ...quote } : {});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [showPrintQuote, setShowPrintQuote] = useState(false);
  const [showMobileMoreDropdown, setShowMobileMoreDropdown] = useState(false);
  const navigate = useNavigate();

  // Update editedQuote when quote prop changes
  useEffect(() => {
    if (quote) {
      setEditedQuote({ ...quote });
      setHasUnsavedChanges(false);
    }
  }, [quote]);

  const handleChange = (e) => {
    setEditedQuote({ ...editedQuote, [e.target.name]: e.target.value });
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => onClose(editedQuote));
    } else {
      onClose(editedQuote);
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    if (pendingAction) {
      pendingAction();
    }
  };

  const handleCancelClose = () => {
    setShowUnsavedWarning(false);
    setPendingAction(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const customerId = quote.customer;
      
      if (!customerId) {
        console.error('No customer ID found in quote:', quote);
        alert('Customer information is missing. Please try again.');
        return;
      }

      const dataToSubmit = {
        ...editedQuote,
        customer: customerId,
        customer_name: quote.customer_name || quote.customer_details?.name
      };

      const updatedQuote = await updateQuote(quote.id, dataToSubmit);
      setHasUnsavedChanges(false);
      if (onClose) {
        onClose(updatedQuote);
      }
    } catch (error) {
      console.error('Error updating quote:', error);
      alert('Failed to update quote');
    }
  };

  const handleCustomerClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => {
        localStorage.setItem('selectedCustomerId', quote.customer ?? quote.customer_details?.id);
        navigate('/accounts');
      });
    } else {
      localStorage.setItem('selectedCustomerId', quote.customer ?? quote.customer_details?.id);
      navigate('/accounts');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTimeDisplay(dateString);
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    return timeString;
  };

  const handleConvertToWorkOrder = () => {
    setShowWorkOrderModal(true);
  };

  const handleWorkOrderCreated = async (workOrder) => {
    console.log('Work order created successfully:', workOrder);
    setShowWorkOrderModal(false);
    
    try {
      // Archive the quote after successful work order creation
      console.log('Archiving quote:', quote.id);
      await archiveQuote(quote.id);
      console.log('Quote archived successfully');
      if (onClose) {
        onClose({ ...quote, is_archived: true });
      }
    } catch (error) {
      console.error('Error archiving quote:', error);
      if (onClose) {
        onClose(quote);
      }
    }
  };

  const handleWorkOrderModalClose = () => {
    setShowWorkOrderModal(false);
  };

  const handlePrintQuote = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowPrintQuote(true));
    } else {
      setShowPrintQuote(true);
    }
  };

  const handleMobileMoreClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMobileMoreDropdown(!showMobileMoreDropdown);
  };

  const handleMobileArchive = () => {
    setEditedQuote({ ...editedQuote, is_archived: !editedQuote.is_archived });
    setHasUnsavedChanges(true);
    setShowMobileMoreDropdown(false);
  };

  const handleMobileConvertToWorkOrder = () => {
    setShowMobileMoreDropdown(false);
    handleConvertToWorkOrder();
  };

  const handleMobilePrintQuote = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMobileMoreDropdown(false);
    
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowPrintQuote(true));
    } else {
      setShowPrintQuote(true);
    }
  };

  return (
    <>
      <form className='quoteorder' onSubmit={handleSubmit}>
      <div className="quoteorder-header-container">
        <h2 className='quoteorderheader'>Quote #{quote.id}</h2>
        <button type="button" className="close-button" onClick={handleClose}>×</button>
      </div>
      <div className='quoteorderform'>
        <div className="quoteordertop">
          <div className='quoteordercontact'>
            <div className='quoteordercontactcolumn'>
              {/* Name */}
              <label className='quoteorderlabelcontact'>
                Name:
                <div 
                  className='quoteorderinputscontact customer-name-link'
                  onClick={handleCustomerClick}
                  style={{ cursor: 'pointer' }}
                >
                  {quote.customer_name || quote.customer_details?.name || 'N/A'}
                </div>
              </label>

              {/* Phone */}
              <label className='quoteorderlabelcontact'>
                Phone:
                <input
                  className='quoteorderinputscontact'
                  type="tel"
                  name="phone"
                  value={editedQuote.phone || ''}
                  onChange={handleChange}
                  pattern="[0-9]{10}"
                  placeholder="1234567890"
                />
              </label>
            </div>
            {/* Vehicle */}
            <label className='quoteorderlabelcontact'>
              Vehicle:
              <input
                className='quoteorderinputscontact'
                type="text"
                name="vehicle"
                value={editedQuote.vehicle || ''}
                onChange={handleChange}
              />
            </label>
          </div>
          

          <div className='quoteorderimportantinfo'>
            {/* Price */}
            <label className='quoteorderlabelimportantinfo'>
              Price:
              <input
                className='quoteorderinputsprice'
                type="number"
                step="0.01"
                name="price"
                value={editedQuote.price || ''}
                onChange={handleChange}
              />
            </label>
            <div>
              <label className='quoteorderlabelimportantinfo'>
                Date Created:
                <span className='quoteorderinputspricedatedcreated'>
                  {formatDateTime(editedQuote.created_at)}
                </span>
              </label>

              {/* Expiration Date - positioned next to Date Created */}
              <label className='quoteorderlabelimportantinfo'>
                Expiration Date:
                <input
                  className='quoteorderinputsprice'
                  type="date"
                  name="expiration_date"
                  value={formatDateForInput(editedQuote.expiration_date)}
                  onChange={(e) => setEditedQuote({ ...editedQuote, expiration_date: e.target.value })}
                />
              </label>
            </div>
            <div>
              {/* Date and Time Promised - grouped together */}
              <label className='quoteorderlabelimportantinfo'>
                Date Promised:
                <input
                  className='quoteorderinputsprice'
                  type="date"
                  name="date_promised"
                  value={formatDateForInput(editedQuote.date_promised)}
                  onChange={(e) => setEditedQuote({ ...editedQuote, date_promised: e.target.value })}
                />
              </label>
              <label className='quoteorderlabelimportantinfo'>
                Time Promised:
                <input
                  className='quoteorderinputsprice'
                  type="time"
                  name="time_promised"
                  value={formatTimeForInput(editedQuote.time_promised)}
                  onChange={(e) => setEditedQuote({ ...editedQuote, time_promised: e.target.value })}
                />
              </label>
            </div>
            <div>
              {/* Drop Off Date and Time - grouped together */}
              <label className='quoteorderlabelimportantinfo'>
                Drop Off Date:
                <input
                  className='quoteorderinputsprice'
                  type="date"
                  name="drop_off_date"
                  value={formatDateForInput(editedQuote.drop_off_date)}
                  onChange={(e) => setEditedQuote({ ...editedQuote, drop_off_date: e.target.value })}
                />
              </label>
              <label className='quoteorderlabelimportantinfo'>
                Drop Off Time:
                <input
                  className='quoteorderinputsprice'
                  type="time"
                  name="drop_off_time"
                  value={formatTimeForInput(editedQuote.drop_off_time)}
                  onChange={(e) => setEditedQuote({ ...editedQuote, drop_off_time: e.target.value })}
                />
              </label>
            </div>
            

          </div>
        </div>

        <div className='quoteorderdetails'>
          <div className="quoteorderdetailssmall">
            {/* Service Requested */}
            <label className='quoteorderlabeldetails'>
              Service Requested:
              <select
                className='quoteorderinputsdetails'
                name="service_requested"
                value={editedQuote.service_requested || ''}
                onChange={(e) => setEditedQuote({ ...editedQuote, service_requested: e.target.value })}
              >
                <option value="Restoration">Restoration</option>
                <option value="Repair Crack">Repair Crack</option>
                <option value="Powder Coating">Powder Coating</option>
                <option value="Straightening">Straightening</option>
                <option value="Mount & Balance">Mount & Balance</option>
                <option value="Other">Other</option>
              </select>
            </label>


            {/* Number of Wheels/Parts */}
            <label className='quoteorderlabeldetails'>
              Number of Wheels/Parts:
              <input
                className='quoteorderinputsdetails'
                type="number"
                name="number_of_wheels"
                value={editedQuote.number_of_wheels || ''}
                onChange={handleChange}
              />
            </label>

            {/* Tires */}
            <label className='quoteorderlabeldetails'>
              Tires:
              <select
                className='quoteorderinputsdetails'
                name="tires"
                value={editedQuote.tires || ''}
                onChange={(e) => setEditedQuote({ ...editedQuote, tires: e.target.value })}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="New">New</option>
              </select>
            </label>


            {/* Center Caps */}
            <label className='quoteorderlabeldetails'>
              Center Caps:
              <select
                className='quoteorderinputsdetails'
                name="center_caps"
                value={editedQuote.center_caps || ''}
                onChange={(e) => setEditedQuote({ ...editedQuote, center_caps: e.target.value })}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
          </div>

          <div className='quoteorderdetailslarge'>
            {/* Description */}
            <label className='quoteorderlabeldetailslarge'>
              Description:
              <textarea
                className='quoteorderinputsdetailslarge'
                name="description"
                value={editedQuote.description || ''}
                onChange={handleChange}
              />
            </label>

            {/* Notes */}
            <label className='quoteorderlabeldetailslarge'>
              Notes:
              <textarea
                className='quoteorderinputsdetailslarge'
                name="notes"
                value={editedQuote.notes || ''}
                onChange={handleChange}
              />
            </label>
          </div>
        </div>
      </div>
      <div className='quoteordersavebuttonbanner'>
        {/* Desktop buttons */}
        <div className='desktop-buttons'>
          <div className='quoteorder-left-buttons'>
            <button 
              type='button' 
              className={`quoteorderhistorybutton ${editedQuote.is_archived ? 'archived' : ''}`}
              onClick={() => {
                setEditedQuote({ ...editedQuote, is_archived: !editedQuote.is_archived });
                setHasUnsavedChanges(true);
              }}
            >
              {editedQuote.is_archived ? 'Unarchive' : 'Archive'}
            </button>
            <button 
              className='quoteorderhistorybutton convert-button' 
              type="button" 
              onClick={handleConvertToWorkOrder}
            >
              Convert to Work Order
            </button>
            <button 
              className='quoteorderhistorybutton' 
              type="button" 
              onClick={handlePrintQuote}
            >
              Print Quote
            </button>
          </div>
        </div>

        {/* Mobile buttons */}
        <div className="mobile-buttons">
          <div className="mobile-more-button-container">
            <button 
              type='button' 
              className='mobile-more-button' 
              onClick={handleMobileMoreClick}
            >
              More
            </button>
            <div className={`mobile-more-dropdown ${showMobileMoreDropdown ? 'show' : ''}`} style={{display: showMobileMoreDropdown ? 'block' : 'none'}}>
              <button 
                type='button' 
                className={`quoteorderhistorybutton ${editedQuote.is_archived ? 'archived' : ''}`}
                onClick={handleMobileArchive}
              >
                {editedQuote.is_archived ? 'Unarchive' : 'Archive'}
              </button>
              <button 
                type='button' 
                className='quoteorderhistorybutton' 
                onClick={handleMobileConvertToWorkOrder}
              >
                Convert to Work Order
              </button>
              <button 
                type='button' 
                className='quoteorderhistorybutton' 
                onClick={handleMobilePrintQuote}
              >
                Print Quote
              </button>
            </div>
          </div>
          <button className='mobile-save-button' type="submit">Save Quote</button>
        </div>

        <button className='quoteordersavebutton' type="submit">Save Quote</button>
      </div>
      {showUnsavedWarning && (
        <div className="unsaved-changes-modal">
          <div className="unsaved-changes-content">
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <div className="unsaved-changes-buttons">
              <button onClick={handleConfirmClose}>Leave</button>
              <button onClick={handleCancelClose}>Stay</button>
            </div>
          </div>
        </div>
      )}
    </form>
    
      <NewWorkOrderModal
        showModal={showWorkOrderModal}
        toggleModal={handleWorkOrderModalClose}
        onWorkOrderCreated={handleWorkOrderCreated}
        initialData={editedQuote}
      />
      {showPrintQuote && (
        <div className="print-quote-overlay">
          <div className="print-quote-container">
            <div className="print-quote-header">
              <h3>Print Quote</h3>
              <div className="print-quote-buttons">
                <button 
                  className="print-button" 
                  onClick={async () => {
                    // Create a new window for printing
                    const printWindow = window.open('', '_blank');
                    const printContent = document.querySelector('.print-quote');
                    
                    if (printContent && printWindow) {
                      // Get the CSS content
                      const cssContent = `
                        /* Print Quote Styles */
                        .print-quote {
                          max-width: 8.5in;
                          margin: 0 auto;
                          padding: 0.5in;
                          font-family: Arial, sans-serif;
                          line-height: 1.4;
                          color: #333;
                          background: white;
                        }
                        
                        /* Header Section */
                        .quote-header {
                          display: flex;
                          justify-content: space-between;
                          align-items: flex-start;
                          margin-bottom: 1rem;
                          position: relative;
                        }
                        
                        .company-address {
                          flex: 1;
                          text-align: left;
                        }
                        
                        .company-address p {
                          margin: 0.1rem 0;
                          font-size: 0.8rem;
                          color: #666;
                          line-height: 1.2;
                        }
                        
                        .company-logo-center {
                          position: absolute;
                          left: 50%;
                          top: 0;
                          transform: translateX(-50%);
                          text-align: center;
                        }
                        
                        .company-logo {
                          max-height: 80px;
                          max-width: 200px;
                          margin: 0;
                        }
                        
                        /* Quote Info Section */
                        .quote-info-section {
                          display: flex;
                          justify-content: flex-end;
                          margin-bottom: 1rem;
                        }
                        
                        .quote-info-right {
                          text-align: right;
                        }
                        
                        .quote-info-item {
                          display: flex;
                          justify-content: space-between;
                          margin-bottom: 0.3rem;
                          font-size: 0.9rem;
                          min-width: 200px;
                        }
                        
                        .quote-info-item .label {
                          font-weight: bold;
                          color: #333;
                          margin-right: 0.5rem;
                        }
                        
                        .quote-info-item .value {
                          color: #333;
                        }
                        
                        /* Bottom Blue Line */
                        .bottom-blue-line {
                          height: 3px;
                          background-color: #2c5aa0;
                          margin: 1rem 0;
                        }
                        
                        /* Bill To Section */
                        .bill-to-section {
                          margin-bottom: 2rem;
                        }
                        
                        .bill-to h3 {
                          color: #2c5aa0;
                          font-size: 1.2rem;
                          margin: 0 0 1rem 0;
                          font-weight: bold;
                        }
                        
                        .customer-info p {
                          margin: 0.3rem 0;
                          font-size: 1rem;
                        }
                        
                        /* Services Section */
                        .services-section {
                          margin-bottom: 2rem;
                        }
                        
                        .services-table {
                          width: 100%;
                          border-collapse: collapse;
                          margin-bottom: 1rem;
                        }
                        
                        .services-table th {
                          background-color: #2c5aa0;
                          color: white;
                          padding: 0.75rem;
                          text-align: left;
                          font-weight: bold;
                          border: 1px solid #2c5aa0;
                        }
                        
                        .services-table td {
                          padding: 0.75rem;
                          border: 1px solid #dee2e6;
                          vertical-align: top;
                        }
                        
                        .services-table tr:nth-child(even) {
                          background-color: #f8f9fa;
                        }
                        
                        .service-description p {
                          margin: 0.2rem 0;
                          font-size: 0.9rem;
                        }
                        
                        /* Totals Section */
                        .totals-section {
                          margin-bottom: 2rem;
                        }
                        
                        .totals-table {
                          max-width: 300px;
                          margin-left: auto;
                        }
                        
                        .total-row {
                          display: flex;
                          justify-content: space-between;
                          padding: 0.5rem 0;
                          border-bottom: 1px solid #dee2e6;
                        }
                        
                        .total-row.total-final {
                          border-top: 2px solid #2c5aa0;
                          border-bottom: 2px solid #2c5aa0;
                          font-size: 1.1rem;
                          margin-top: 0.5rem;
                          padding-top: 0.75rem;
                          padding-bottom: 0.75rem;
                        }
                        
                        .total-row .label {
                          font-weight: bold;
                        }
                        
                        .total-row .value {
                          font-weight: bold;
                        }
                        
                        
                        /* Notes Section */
                        .notes-section {
                          margin-bottom: 2rem;
                          padding: 1rem;
                          background-color: #f8f9fa;
                          border-left: 4px solid #2c5aa0;
                        }
                        
                        .notes-section h3 {
                          color: #2c5aa0;
                          font-size: 1.1rem;
                          margin: 0 0 0.5rem 0;
                          font-weight: bold;
                        }
                        
                        .notes-textarea {
                          width: 100%;
                          min-height: 80px;
                          padding: 0.75rem;
                          border: 1px solid #dee2e6;
                          border-radius: 4px;
                          font-size: 0.9rem;
                          font-family: inherit;
                          resize: vertical;
                          background-color: white;
                          transition: border-color 0.2s ease;
                        }
                        
                        .notes-textarea:focus {
                          outline: none;
                          border-color: #2c5aa0;
                          box-shadow: 0 0 0 2px rgba(44, 90, 160, 0.2);
                        }
                        
                        .notes-textarea::placeholder {
                          color: #6c757d;
                          font-style: italic;
                        }
                        
                        /* Footer Section */
                        .footer-section {
                          display: flex;
                          justify-content: space-between;
                          align-items: flex-start;
                          margin-top: 3rem;
                          padding-top: 2rem;
                          border-top: 1px solid #dee2e6;
                        }
                        
                        .payment-terms h4 {
                          color: #2c5aa0;
                          font-size: 1.1rem;
                          margin: 0 0 0.5rem 0;
                          font-weight: bold;
                        }
                        
                        .payment-terms p {
                          margin: 0.2rem 0;
                          font-size: 0.9rem;
                        }
                        
                        .contact-info {
                          text-align: right;
                        }
                        
                        .contact-info p {
                          margin: 0.2rem 0;
                          font-size: 0.9rem;
                          color: #666;
                        }
                      `;
                      
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Quote - Quote #${editedQuote.id}</title>
                            <style>${cssContent}</style>
                          </head>
                          <body>
                            ${printContent.outerHTML}
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.focus();
                      printWindow.print();
                      printWindow.close();
                    }
                  }}
                >
                  Print
                </button>
                <button 
                  className="quoteorderhistorybutton" 
                  onClick={() => setShowPrintQuote(false)}
                >
                  Close
                </button>
              </div>
            </div>
            <PrintQuote quote={editedQuote} />
          </div>
        </div>
      )}
    </>
  );
};

export default QuoteOrder; 